/**
 * The signed-in session, following Identity's integration guide:
 *
 * * The access token lives in memory only (never localStorage).
 * * Identity's HttpOnly refresh cookie survives reloads and is shared by every CortexGrip app,
 *   so on start-up we call `/v1/auth/refresh` and are signed in if the user already is anywhere.
 * * Tokens are refreshed ~1 minute before `expires_in` elapses; concurrent refreshes are coalesced.
 *   Only a *refusal* (Identity says the session is over) ends the session: a network error or a 5xx
 *   keeps it and retries with backoff.
 *
 * Session expiry without losing work (PHP-3322, QA-30):
 *
 * * What ends a customer's session is Identity's `session_expires_at` (idle 14 days, sliding on every
 *   refresh, and an absolute 30 days) or a revocation (signed out elsewhere, password change...). There
 *   is no client-side idle timeout. `session_expires_at` is on every token response.
 * * `warning` appears `warningMs` before that end, with "Stay signed in" (a refresh).
 * * Ending never unmounts the page. The status turns `expired` (the user is kept, the token is dropped)
 *   and `<SessionGuard/>` lays the re-login prompt over it. Requests that got a 401 wait in
 *   `waitForReauth()` and are replayed once after the same user signs in again.
 * * Signing out in another tab ends the session here the same way (the page stays under the prompt).
 */
import { config } from "./config";
import { ApiError, request } from "./http";
import type { TokensOut, User } from "./types";

/** revoked: the API said the session was revoked · ended: Identity refused the refresh. */
export type ExpiryReason = "ended" | "revoked" | "signed-out-elsewhere";

export interface SessionWarning {
  /** When the session ends, epoch ms. */
  endsAt: number;
  /** False once "Stay signed in" showed the end cannot be pushed back (the absolute limit). */
  extendable: boolean;
}

export type SessionState =
  | { status: "loading" }
  | { status: "signed_out" }
  | {
      status: "signed_in";
      user: User;
      accessToken: string;
      expiresAt: number;
      /** Set while the session's end is near. */
      warning: SessionWarning | null;
    }
  | {
      /** The session ended but the page stays: the user is kept for `useUser()`, the token is gone. */
      status: "expired";
      user: User;
      reason: ExpiryReason;
      /** The prompt was put off ("Not now"): background reads fail quietly until sign-in. */
      dismissed: boolean;
      /** Identity's cookie now belongs to a different user: this page must be reset, not continued. */
      switchedUser: boolean;
    };

/** ok · refused: Identity ended the session · switched: the cookie belongs to someone else now · unreachable: transient. */
export type ResumeResult = "ok" | "refused" | "switched" | "unreachable";
export type StayResult = "extended" | "limit" | "unreachable" | "ended" | "switched";

type Listener = () => void;

const REFRESH_EARLY_MS = 60_000;
/** Retry delays after a transient refresh failure; the last one repeats. */
const RETRY_MS = [5_000, 15_000, 30_000, 60_000];
/** setTimeout overflows past ~24.8 days and sessions last 30: wake up at least this often and re-check. */
const MAX_DELAY_MS = 60 * 60_000;

/** Identity said no, as opposed to "could not ask": 4xx other than timeouts and rate limits. */
function isRefusal(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status >= 400 &&
    err.status < 500 &&
    err.status !== 408 &&
    err.status !== 425 &&
    err.status !== 429
  );
}

function sameWarning(a: SessionWarning | null, b: SessionWarning | null): boolean {
  return a === b || (!!a && !!b && a.endsAt === b.endsAt && a.extendable === b.extendable);
}

export class Session {
  private state: SessionState = { status: "loading" };
  private listeners = new Set<Listener>();
  private reauthListeners = new Set<Listener>();
  private refreshing: Promise<ResumeResult> | null = null;
  private resuming: Promise<boolean> | null = null;
  private waiters: ((ok: boolean) => void)[] = [];
  /** Refreshes the access token. */
  private timer: ReturnType<typeof setTimeout> | null = null;
  /** Shows the warning and ends the session. */
  private sessionTimer: ReturnType<typeof setTimeout> | null = null;
  private retries = 0;
  /** Identity's `session_expires_at`, epoch ms. */
  private sessionEndsAt: number | null = null;
  private sessionBegan = 0;
  /** An end we tried to push back and could not (the absolute limit). */
  private unextendableEnd: number | null = null;
  /** An end that has already passed on this clock; asked Identity once before believing it. */
  private verifiedEnd: number | null = null;
  /** Bumped by sign-out, so a refresh that was in flight cannot sign the user back in. */
  private epoch = 0;
  private channel: BroadcastChannel | null = null;
  private readonly authUrl: string;
  private readonly now: () => number;
  private readonly warningMs: number;

  constructor(
    authUrl: string,
    now: () => number = () => Date.now(), // looked up each time, so a fake clock applies
    warningMs: number = config.sessionWarningSeconds * 1000,
  ) {
    this.authUrl = authUrl;
    this.now = now;
    this.warningMs = warningMs;
    if (typeof BroadcastChannel !== "undefined") {
      this.channel = new BroadcastChannel("cortexgrip-auth");
      this.channel.onmessage = (e: MessageEvent<string>) => this.onMessage(e.data);
    }
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", this.onWake);
  }

  /** Stop listening (tests that build their own Session). */
  destroy(): void {
    this.clearTimers();
    document.removeEventListener("visibilitychange", this.onWake);
    this.channel?.close();
    this.channel = null;
  }

  // ── store protocol (useSyncExternalStore) ────────────────────────────────
  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): SessionState => this.state;

  private set(state: SessionState): void {
    this.state = state;
    for (const l of this.listeners) l();
  }

  /** Called after the same user signed in again over an expired page (here or in another tab). */
  onReauthenticated(listener: Listener): () => void {
    this.reauthListeners.add(listener);
    return () => this.reauthListeners.delete(listener);
  }

  // ── cross-tab ────────────────────────────────────────────────────────────
  private onMessage(message: string): void {
    const s = this.state;
    if (message === "signed_out") {
      // The page stays: end the session here (the token is dropped) and ask for a sign-in over it.
      if (s.status === "signed_in") this.expire("signed-out-elsewhere");
      else if (s.status !== "expired") this.clear();
    }
    if (message === "signed_in" && s.status !== "signed_in") {
      if (s.status === "expired" && s.switchedUser) return;
      void this.resume();
    }
  }

  /** The tab was asleep or hidden: timers may have run late, so re-check now. */
  private onWake = (): void => {
    if (document.visibilityState !== "visible") return;
    const s = this.state;
    if (s.status !== "signed_in") return;
    if (s.expiresAt - this.now() <= REFRESH_EARLY_MS) void this.resume();
    this.evaluate();
  };

  // ── lifecycle ────────────────────────────────────────────────────────────
  /** The user this page belongs to, once there is one. */
  private knownUser(): User | null {
    return this.state.status === "signed_in" || this.state.status === "expired" ? this.state.user : null;
  }

  /** Adopt freshly issued tokens (after login, MFA, register, or refresh). */
  accept(tokens: TokensOut): void {
    const prev = this.state;
    const now = this.now();
    const known = this.knownUser();
    const end = Date.parse(tokens.session_expires_at);
    this.sessionEndsAt = Number.isFinite(end) ? end : null;
    if (prev.status !== "signed_in") this.sessionBegan = now;
    this.retries = 0;
    const expiresAt = now + tokens.expires_in * 1000;
    this.set({
      status: "signed_in",
      user: tokens.user,
      accessToken: tokens.access_token,
      expiresAt,
      warning: this.warningFor(now),
    });
    this.scheduleRefresh(Math.max(5_000, expiresAt - now - REFRESH_EARLY_MS));
    this.evaluate();
    if (prev.status === "expired") {
      // Back in: the same user carries on (replay what was waiting); anyone else starts over.
      const carryOn = known?.id === tokens.user.id && this.state.status === "signed_in";
      this.settleWaiters(carryOn);
      if (carryOn) for (const l of this.reauthListeners) l();
    }
  }

  updateUser(user: User): void {
    if (this.state.status === "signed_in") this.set({ ...this.state, user });
  }

  private clearTimers(): void {
    if (this.timer) clearTimeout(this.timer);
    if (this.sessionTimer) clearTimeout(this.sessionTimer);
    this.timer = this.sessionTimer = null;
  }

  private scheduleRefresh(delay: number): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.resume(), Math.min(delay, MAX_DELAY_MS));
  }

  // ── the session's end ────────────────────────────────────────────────────
  /** How long before the end the warning shows: never longer than the session itself. */
  private lead(end: number): number {
    return Math.min(this.warningMs, Math.max(0, end - this.sessionBegan));
  }

  private warningFor(now: number): SessionWarning | null {
    const end = this.sessionEndsAt;
    if (end === null || now < end - this.lead(end) || now >= end) return null;
    return { endsAt: end, extendable: this.unextendableEnd !== end };
  }

  /** Bring the warning up to date and set the one timer for the next change. */
  private evaluate(): void {
    if (this.sessionTimer) clearTimeout(this.sessionTimer);
    this.sessionTimer = null;
    const s = this.state;
    const end = this.sessionEndsAt;
    if (s.status !== "signed_in" || end === null) return;
    const now = this.now();
    if (now >= end) {
      // Our clock says it is over. Ask Identity once before believing it (clock skew, a slid end).
      if (this.verifiedEnd === end) {
        this.expire("ended");
        return;
      }
      this.verifiedEnd = end;
      void this.resume().then((result) => {
        if (result === "unreachable") this.expire("ended");
        else if (result === "ok") this.evaluate();
      });
      return;
    }
    const warning = this.warningFor(now);
    if (!sameWarning(s.warning, warning)) this.set({ ...s, warning });
    const next = warning ? end : end - this.lead(end);
    this.sessionTimer = setTimeout(() => this.evaluate(), Math.min(next - now, MAX_DELAY_MS));
  }

  /** End the session over the current page: the user is kept, the token is dropped. */
  private expire(reason: ExpiryReason): void {
    const s = this.state;
    if (s.status !== "signed_in") return;
    this.clearTimers();
    this.set({ status: "expired", user: s.user, reason, dismissed: false, switchedUser: false });
  }

  /** Identity's cookie belongs to another user now: never continue this page as them. */
  private userSwitched(): void {
    const user = this.knownUser();
    if (!user) return;
    this.clearTimers();
    this.set({ status: "expired", user, reason: "ended", dismissed: false, switchedUser: true });
    this.settleWaiters(false);
  }

  // ── refresh ──────────────────────────────────────────────────────────────
  /**
   * Exchange the refresh cookie for a new access token. One at a time (Identity rotates the refresh
   * token, so parallel calls would race). A refusal ends the session; a transient failure does not.
   */
  resume(): Promise<ResumeResult> {
    this.refreshing ??= this.exchange().finally(() => {
      this.refreshing = null;
    });
    return this.refreshing;
  }

  private async exchange(): Promise<ResumeResult> {
    const epoch = this.epoch;
    let tokens: TokensOut;
    try {
      tokens = await request<TokensOut>(this.authUrl, "/v1/auth/refresh", {
        method: "POST",
        credentials: "include",
      });
    } catch (err) {
      if (epoch !== this.epoch) return "refused";
      return this.failed(isRefusal(err));
    }
    if (epoch !== this.epoch) return "refused"; // signed out while it was in flight
    const known = this.knownUser();
    if (known && known.id !== tokens.user.id) {
      this.userSwitched();
      return "switched";
    }
    this.accept(tokens);
    return "ok";
  }

  private failed(refused: boolean): ResumeResult {
    const s = this.state;
    if (s.status === "signed_in") {
      if (refused) this.expire("ended");
      else this.scheduleRefresh(RETRY_MS[Math.min(this.retries++, RETRY_MS.length - 1)]!);
    } else if (s.status !== "expired") {
      this.clear(); // start-up: there is nothing to lose before the first sign-in
    }
    return refused ? "refused" : "unreachable";
  }

  /** Exchange the refresh cookie for a new access token. Resolves to whether we are signed in. */
  async refresh(): Promise<boolean> {
    return (await this.resume()) === "ok" && this.state.status === "signed_in";
  }

  /**
   * A valid access token, refreshing first if it is about to expire. Null when there is no session (or
   * it just ended); throws a network error when the token ran out and Identity cannot be reached.
   */
  async token(): Promise<string | null> {
    const s = this.state;
    if (s.status === "signed_in" && s.expiresAt - this.now() > 10_000) return s.accessToken;
    if (s.status === "signed_out" || s.status === "expired") return null;
    const result = await this.resume();
    const now = this.state;
    if (result === "ok" && now.status === "signed_in") return now.accessToken;
    if (result === "unreachable" && now.status === "signed_in") {
      if (now.expiresAt > this.now()) return now.accessToken;
      throw new ApiError({
        status: 0,
        reason: "NETWORK_ERROR",
        detail: "Could not reach the server. Check your connection.",
      });
    }
    return null;
  }

  /** "Stay signed in": a refresh, which also slides Identity's idle end. */
  async staySignedIn(): Promise<StayResult> {
    if (this.state.status !== "signed_in") return "ended";
    const result = await this.resume();
    if (result === "switched") return "switched";
    if (result === "refused") return "ended";
    if (result === "unreachable") return "unreachable";
    const s = this.state;
    if (s.status === "signed_in" && s.warning) {
      // Still inside the warning window after a refresh: the end is a hard limit that cannot move.
      this.unextendableEnd = s.warning.endsAt;
      this.set({ ...s, warning: { endsAt: s.warning.endsAt, extendable: false } });
      return "limit";
    }
    return "extended";
  }

  // ── requests that got a 401 ──────────────────────────────────────────────
  private settleWaiters(ok: boolean): void {
    const waiting = this.waiters;
    this.waiters = [];
    for (const resolve of waiting) resolve(ok);
  }

  private park(): Promise<boolean> {
    return new Promise((resolve) => this.waiters.push(resolve));
  }

  /**
   * A request got a 401. Resolves true once the same user is signed in again (replay it), or false
   * when there is nothing to wait for (no session, the prompt was put off, someone else signed in).
   *
   * Concurrent 401s share one refresh and one prompt. `background` reads (GETs) do not reopen a prompt
   * the user put off. `revoked`: the API said the session was revoked, so a refresh is pointless.
   */
  waitForReauth(opts: { background?: boolean; revoked?: boolean } = {}): Promise<boolean> {
    const s = this.state;
    if (s.status === "expired") {
      if (s.switchedUser) return Promise.resolve(false);
      if (s.dismissed) {
        if (opts.background) return Promise.resolve(false);
        this.set({ ...s, dismissed: false });
      }
      return this.park();
    }
    if (s.status !== "signed_in") return Promise.resolve(false);
    if (opts.revoked) {
      this.expire("revoked");
      return this.park();
    }
    // As far as we know the session is alive: the token may just be stale, so try the cookie once.
    this.resuming ??= this.resume().then((result) => {
      this.resuming = null;
      if (result === "ok") return true;
      const now = this.state;
      // Unreachable, signed out or put off while it ran, or someone else's cookie: nothing to wait for.
      if (result !== "refused" || now.status !== "expired" || now.dismissed || now.switchedUser) return false;
      return this.park();
    });
    return this.resuming;
  }

  /** "Not now" on the re-login prompt: parked requests fail; the page stays usable to copy work out. */
  postponeReauth(): void {
    const s = this.state;
    if (s.status !== "expired") return;
    this.set({ ...s, dismissed: true });
    this.settleWaiters(false);
  }

  /** Re-open the prompt after "Not now". */
  resumeReauth(): void {
    const s = this.state;
    if (s.status === "expired" && s.dismissed) this.set({ ...s, dismissed: false });
  }

  /**
   * Finish signing in again over an expired page, with the tokens a password or MFA step returned.
   * "other-user": it is someone else's account: nothing changed, and the page is not theirs to continue.
   */
  completeReauth(tokens: TokensOut): "ok" | "other-user" {
    const known = this.knownUser();
    if (known && known.id !== tokens.user.id) return "other-user";
    this.accept(tokens);
    this.announceSignIn();
    return "ok";
  }

  /** Sign out the other account the prompt signed in as, to try again as the right one. */
  async discardOtherUser(): Promise<void> {
    await request(this.authUrl, "/v1/auth/logout", { method: "POST", credentials: "include" }).catch(
      () => undefined,
    );
  }

  announceSignIn(): void {
    this.channel?.postMessage("signed_in");
  }

  async signOut(): Promise<void> {
    this.epoch++;
    try {
      await request(this.authUrl, "/v1/auth/logout", { method: "POST", credentials: "include" });
    } finally {
      this.clear();
      this.channel?.postMessage("signed_out");
    }
  }

  /** Forget the session: the explicit sign-out (and "no session" at start-up), which does unmount. */
  clear(): void {
    this.epoch++;
    this.clearTimers();
    this.sessionEndsAt = null;
    this.unextendableEnd = this.verifiedEnd = null;
    this.set({ status: "signed_out" });
    this.settleWaiters(false);
  }
}
